import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DrawerRight from 'Components/DrawerRight'
import SPMActivityDialog from 'Components/SPMActivityDialog'
import type { ActivityData } from 'Components/SPMActivityDialog/SPMActivityDialog'
import SPMActivityDialogButton from 'Components/SPMActivityDialogButton'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import SPMAllowedApiClients from 'Services/ServiceProviderManagement/Features/AllowedClient/Components/SPMAllowedApiClients'
import DeviceDetails from 'Services/ServiceProviderManagement/Features/Device/Components/DeviceDetails'
import {
  useGetSingleDeviceQuery,
  useUpdateSingleDeviceMutation
} from 'Services/ServiceProviderManagement/Features/Device/deviceApi'
import { SELECTED_DEVICE_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/Device/Hooks/useDevicesWithParams'
import { DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/Device/Pages/DeviceListPage'
import {
  type Device,
  DeviceTabType
} from 'Services/ServiceProviderManagement/Features/Device/types'
import { SERVICE_PROVIDER_MANAGEMENT_ROOT_URL } from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Button, Divider, Tab } from '@mui/material'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface DeviceDrawerProps {
  open: boolean
  handleClose: () => void
  selectedDeviceId: string | undefined
}

function DeviceDrawer({ open, handleClose, selectedDeviceId }: Readonly<DeviceDrawerProps>) {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/devices`
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activityDialogOpen, setActivityDialogOpen] = useState(false)

  const deviceDetailsDrawerTabFromParams =
    searchParams.get(DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY) ?? DeviceTabType.DETAILS

  const { data: selectedDevice, isFetching: isSingleDeviceFetching } = useGetSingleDeviceQuery(
    selectedDeviceId ?? '',
    {
      skip: !selectedDeviceId
    }
  )
  const [updateSingleDevice, { isLoading: isSubmitLoading }] = useUpdateSingleDeviceMutation()

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    selectedDevice?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    selectedDevice?.client_write ?? []
  )

  useEffect(() => {
    if (open && selectedDevice) {
      setSelectedWriteClients(selectedDevice.client_write ?? [])
      setSelectedReadClients(selectedDevice.client_read ?? [])
    }
  }, [selectedDevice, open])

  const handleDeviceDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: DeviceTabType
  ) => {
    searchParams.set(DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleActivityDialogOpen = () => {
    setActivityDialogOpen(true)
  }

  const handleActivityDialogClose = () => {
    setActivityDialogOpen(false)
  }

  const handleActivityDialogSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    activityData: ActivityData
  ) => {
    e.preventDefault()
    const deviceData = {
      ...selectedDevice,
      ...activityData
    } as Device

    updateSingleDevice({
      ...deviceData,
      id: deviceData.device_id
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Device successfully saved.`)
        handleActivityDialogClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error saving the device.`)
      })
  }

  const handleAllowedApiClientsSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedDevice) return

    const deviceData = {
      ...selectedDevice,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    updateSingleDevice({
      ...deviceData,
      id: selectedDevice.device_id
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Device successfully saved.`)
        const selectedDeviceParam = `${SELECTED_DEVICE_PARAMETER_KEY}=${selectedDevice.device_id}`
        const detailsDrawerTabParam = `${DEVICE_DETAILS_DRAWER_TAB_PARAMETER_KEY}=${DeviceTabType.ALLOWED_API_CLIENTS}`
        navigate(`${baseUrl}/?${selectedDeviceParam}&${detailsDrawerTabParam}`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error saving the device.`)
      })
  }

  return (
    <DrawerRight
      open={open}
      onClose={handleClose}
      topBar={
        <Box>
          {selectedDevice && (
            <SPMActivityDialogButton
              activityEndDate={selectedDevice.activity_end_date?.toString()}
              isActive={selectedDevice.active}
              isLoading={isSubmitLoading}
              onClick={handleActivityDialogOpen}
              sx={{ mr: 1 }}
            />
          )}
          <CustomButton
            onClick={() => navigate(`${baseUrl}/${selectedDeviceId}/edit`)}
            size="small"
            startIcon={<EditIcon />}
          >{t`Edit`}</CustomButton>
        </Box>
      }
    >
      <WithLoader loading={isSingleDeviceFetching}>
        {selectedDevice && (
          <TabContext value={deviceDetailsDrawerTabFromParams}>
            <TabList aria-label={t`Device tab list`} onChange={handleDeviceDetailsDrawerTabChange}>
              <Tab label={t`Details`} value={DeviceTabType.DETAILS} />
              <Tab label={t`Allowed API clients`} value={DeviceTabType.ALLOWED_API_CLIENTS} />
            </TabList>
            <Divider />
            <TabPanel sx={{ p: 0 }} value={DeviceTabType.DETAILS}>
              <DeviceDetails device={selectedDevice} />
            </TabPanel>
            <TabPanel sx={{ p: 0 }} value={DeviceTabType.ALLOWED_API_CLIENTS}>
              <StyledForm onSubmit={handleAllowedApiClientsSave} isDisabled={isSubmitLoading}>
                <SPMAllowedApiClients
                  selectedReadClients={selectedReadClients}
                  selectedWriteClients={selectedWriteClients}
                  setSelectedReadClients={setSelectedReadClients}
                  setSelectedWriteClients={setSelectedWriteClients}
                />
                <Box
                  sx={{
                    mt: 2,
                    mx: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    columnGap: 2
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      color="inherit"
                      sx={{ width: '100%' }}
                    >
                      {t`Cancel`}
                    </Button>
                    <CustomLoadingButton
                      isLoading={isSubmitLoading}
                      type="submit"
                      sx={{ width: '100%' }}
                      icon={<SaveIcon />}
                      iconPosition="start"
                    >
                      {t`Save changes`}
                    </CustomLoadingButton>
                  </Box>
                </Box>
              </StyledForm>
            </TabPanel>
          </TabContext>
        )}
      </WithLoader>
      {selectedDevice && (
        <SPMActivityDialog
          open={activityDialogOpen}
          handleClose={handleActivityDialogClose}
          originalActivityEndDate={selectedDevice.activity_end_date?.toString()}
          isActive={selectedDevice.active}
          onSubmit={handleActivityDialogSubmit}
          spmObjectName={t`device`}
          isSubmitLoading={isSubmitLoading}
        />
      )}
    </DrawerRight>
  )
}

export default DeviceDrawer
